import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { X, Crown, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

const PERKS = [
  "Saved taste preferences & allergies",
  "Full conversation history",
  "Pantry memory — cook with what you own",
  "Favorite recipes, saved forever",
  "Household profiles for the whole family",
  "Personalized recommendations from Loretta",
];

export function PremiumModal({ open, onClose }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState("premium_yearly");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) api.get("/payments/config").then(({ data }) => setPlans(data.plans || []));
  }, [open]);

  if (!open) return null;

  const checkout = async () => {
    if (!user) {
      onClose();
      navigate("/login?next=premium");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: selected,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Couldn't start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-espresso/50 p-4 backdrop-blur-sm" data-testid="premium-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="relative w-full max-w-lg overflow-hidden rounded-3xl bg-cream shadow-[0_20px_40px_rgba(44,30,22,0.2)]"
      >
        <button onClick={onClose} data-testid="premium-close" className="absolute right-4 top-4 z-10 text-cream/80 hover:text-white">
          <X className="h-5 w-5" />
        </button>
        <div className="bg-loretta-olive px-7 py-8 text-cream">
          <Crown className="mb-3 h-9 w-9 text-loretta-accent" />
          <h2 className="font-serif text-3xl font-bold text-cream">Aunt Lorettas Kitchen Chat Premium</h2>
          <p className="mt-2 text-cream/85">Free chat is always unlimited. Premium gives Loretta a memory of you.</p>
        </div>

        <div className="p-7">
          <ul className="mb-6 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {PERKS.map((p) => (
              <li key={p} className="flex items-start gap-2 text-sm text-espressolt">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-loretta-olive" /> {p}
              </li>
            ))}
          </ul>

          <div className="mb-5 flex gap-3">
            {plans.map((pl) => (
              <button
                key={pl.lookup_key}
                onClick={() => setSelected(pl.lookup_key)}
                data-testid={`plan-${pl.lookup_key}`}
                className={`relative flex-1 rounded-2xl border-2 p-4 text-left transition-colors ${
                  selected === pl.lookup_key ? "border-loretta-red bg-loretta-red/5" : "border-espresso/10 hover:border-loretta-red/40"
                }`}
              >
                {pl.badge && (
                  <span className="absolute -top-2.5 right-3 rounded-full bg-loretta-red px-2 py-0.5 text-[10px] font-bold text-white">{pl.badge}</span>
                )}
                <p className="text-sm font-medium text-espressolt">{pl.label}</p>
                <p className="font-serif text-2xl font-bold text-espresso">{pl.price}<span className="text-sm font-normal text-espresso/50">{pl.period}</span></p>
              </button>
            ))}
          </div>

          <button
            onClick={checkout}
            disabled={loading}
            data-testid="premium-checkout-button"
            className="w-full rounded-full bg-loretta-red py-3.5 font-semibold text-white transition-colors hover:bg-loretta-redhover disabled:opacity-60"
          >
            {loading ? "Taking you to checkout…" : user ? "Upgrade to Premium" : "Sign in to upgrade"}
          </button>
          <p className="mt-3 text-center text-xs text-espresso/40">Test card 4242 4242 4242 4242 · any future date · any CVC</p>
        </div>
      </motion.div>
    </div>
  );
}
